import { useCallback, useMemo, useState } from "react";
import { Component, ComponentType } from "../../../../types/component";
import {
  getComponentOffWingType,
  OffWingTypeOption,
} from "../../../../utils/offWingType";
import type { AttachmentListFilter } from "./useComponents";

export function useComponentFilter(
  components: Component[],
  attachmentListFilter: AttachmentListFilter,
  setAttachmentListFilter: (v: AttachmentListFilter) => void
) {
  const [filter, setFilter] = useState<ComponentType | "All">("All");
  const [offWingTypeFilters, setOffWingTypeFilters] = useState<
    Set<OffWingTypeOption>
  >(() => new Set());
  const [operatorFilters, setOperatorFilters] = useState<Set<string>>(
    () => new Set()
  );

  const toggleOffWingTypeFilter = useCallback(
    (type: OffWingTypeOption) => {
      const isChecking = !offWingTypeFilters.has(type);
      setOffWingTypeFilters((prev) => {
        const next = new Set(prev);
        if (isChecking) {
          next.add(type);
        } else {
          next.delete(type);
        }
        return next;
      });
      if (isChecking && attachmentListFilter !== "nonAttached") {
        setAttachmentListFilter("nonAttached");
      }
    },
    [offWingTypeFilters, attachmentListFilter, setAttachmentListFilter]
  );

  const clearOffWingTypeFilters = useCallback(() => {
    setOffWingTypeFilters(new Set());
  }, []);

  const toggleOperatorFilter = useCallback(
    (id: string) => {
      const isChecking = !operatorFilters.has(id);
      setOperatorFilters((prev) => {
        const next = new Set(prev);
        if (isChecking) {
          next.add(id);
        } else {
          next.delete(id);
        }
        return next;
      });
      // Operator info is only meaningful for off-wing assemblies
      if (isChecking && attachmentListFilter !== "nonAttached") {
        setAttachmentListFilter("nonAttached");
      }
    },
    [operatorFilters, attachmentListFilter, setAttachmentListFilter]
  );

  const clearOperatorFilters = useCallback(() => {
    setOperatorFilters(new Set());
  }, []);

  const filteredComponents = useMemo(() => {
    let result =
      filter === "All"
        ? components
        : components.filter((c) => c.type === filter);

    if (offWingTypeFilters.size > 0) {
      result = result.filter((c) =>
        offWingTypeFilters.has(getComponentOffWingType(c))
      );
    }

    if (operatorFilters.size > 0) {
      result = result.filter(
        (c) => c.operatorId != null && operatorFilters.has(c.operatorId)
      );
    }

    return result;
  }, [filter, components, offWingTypeFilters, operatorFilters]);

  return {
    filter,
    setFilter,
    filteredComponents,
    offWingTypeFilters,
    toggleOffWingTypeFilter,
    clearOffWingTypeFilters,
    operatorFilters,
    toggleOperatorFilter,
    clearOperatorFilters,
  };
}
